import axios from 'axios';
import { API_URL } from '../utils/constants';

// Debug: Log API configuration
const baseURL = `${API_URL}/api`;
console.log('[DEBUG] ========== API SERVICE INITIALIZATION ==========');
console.log('[DEBUG] API Configuration:', {
  VITE_API_URL_env: import.meta.env.VITE_API_URL,
  API_URL_constant: API_URL,
  baseURL: baseURL,
  fullColorProfileURL: `${baseURL}/color-profile`,
  fullUsersMeURL: `${baseURL}/users/me`
});
console.log('[DEBUG] ================================================');

// Validate API_URL is not a malformed relative path (empty = same-origin / Vite proxy, OK in DEV)
if (API_URL !== '' && (API_URL.startsWith('/') || !API_URL.startsWith('http'))) {
  console.error('[ERROR] Invalid API_URL configuration!');
  console.error('API_URL must be a full URL like http://localhost:8000, or empty for same-origin proxy');
  console.error('Current API_URL:', API_URL);
  console.error('This will cause requests to hit the wrong server!');
}

// Create axios instance
const api = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('firebase_id_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData (browser will set it with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // Debug: Log the full request URL
    const fullUrl = `${config.baseURL}${config.url}`;
    console.log('[DEBUG] API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: fullUrl,
      hasToken: !!token
    });
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Validate response is JSON, not HTML
    const contentType = response.headers['content-type'] || '';
    
    // Check if response is HTML (this should never happen if API is configured correctly)
    if (response.data && typeof response.data === 'string') {
      const trimmed = response.data.trim();
      if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
        console.error('[ERROR] API returned HTML instead of JSON');
        console.error('Response URL:', response.config?.url);
        console.error('Response baseURL:', response.config?.baseURL);
        console.error('Full URL:', response.config?.baseURL + response.config?.url);
        console.error('Response status:', response.status);
        console.error('Content-Type:', contentType);
        console.error('Response preview:', trimmed.substring(0, 200));
        console.error('');
        console.error('TROUBLESHOOTING:');
        console.error('1. Check if backend is running: curl http://localhost:8000/api/health');
        console.error('2. Check VITE_API_URL:', import.meta.env.VITE_API_URL || 'http://localhost:8000');
        console.error('3. Restart frontend dev server after changing .env file');
        console.error('4. Check browser Network tab to see actual request URL');
        return Promise.reject(new Error('Invalid response: received HTML instead of JSON. The API request is hitting the wrong server. Check API configuration and restart frontend dev server.'));
      }
    }
    
    // Validate Content-Type header
    if (contentType && !contentType.includes('application/json') && !contentType.includes('text/json')) {
      console.warn('[WARNING] Unexpected Content-Type:', contentType);
    }
    
    return response;
  },
  async (error) => {
    // Log detailed error information
    if (error.response) {
      console.error('[ERROR] API Error Response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        data: error.response.data
      });
      
      // Check if response is HTML (error page)
      if (error.response.data && typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE')) {
        console.error('[ERROR] Server returned HTML error page instead of JSON');
        console.error('This usually means:');
        console.error('  1. The API endpoint does not exist (404)');
        console.error('  2. The backend server is not running');
        console.error('  3. There is a routing issue');
        console.error('Full URL:', error.config?.baseURL + error.config?.url);
      }
    } else if (error.request) {
      console.error('[ERROR] No response received from server:', {
        url: error.config?.url,
        baseURL: error.config?.baseURL
      });
    }
    
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('firebase_id_token');
      localStorage.removeItem('user');
      // Redirect to sign in will be handled by AuthContext
    }
    return Promise.reject(error);
  }
);

// Health check function
export const checkBackendHealth = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/health`, {
      timeout: 5000, // 5 second timeout for health check
    });
    return { healthy: true, data: response.data };
  } catch (error) {
    console.error('[ERROR] Backend health check failed:', error);
    return { 
      healthy: false, 
      error: error.message,
      code: error.code,
      isNetworkError: !error.response && error.request
    };
  }
};

export default api;

